import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { Ban, AlertTriangle, Clock, ShieldCheck } from "lucide-react";

export const OtherTab = ({
  formData,
  handleChange,
}) => {
  // Helper to determine current status
  const getCurrentStatus = () => {
    if (formData.is_blocked) return 'blocked';
    if (formData.is_discontinued) return 'discontinued';
    if (formData.is_temp_blocked) return 'temp_blocked';
    return 'active';
  };

  const handleStatusChange = (status) => {
    if (status === 'active') {
      handleChange('is_blocked', false);
      handleChange('is_discontinued', false);
      handleChange('is_temp_blocked', false);
      handleChange('status_reason', null);
    } else if (status === 'blocked') {
      handleChange('is_blocked', true);
      handleChange('is_discontinued', false);
      handleChange('is_temp_blocked', false);
    } else if (status === 'discontinued') {
      handleChange('is_blocked', false);
      handleChange('is_discontinued', true);
      handleChange('is_temp_blocked', false);
    } else if (status === 'temp_blocked') {
      handleChange('is_blocked', false);
      handleChange('is_discontinued', false);
      handleChange('is_temp_blocked', true);
    }
  };

  const currentStatus = getCurrentStatus();

  return (
    <div className="space-y-6">
      {/* Online shop visibility */}
      <div className="p-4 border rounded-lg bg-orange-50 border-orange-200" data-testid="shop-visibility-section">
        <Label className="text-base font-semibold mb-1 block">Online shop</Label>
        <p className="text-xs text-gray-600 mb-2">Auto = shows online while in stock. If the shop is set to "Only products I choose", pick "Always show online" for the products you want listed.</p>
        <select
          value={formData.shop_visibility || "auto"}
          onChange={(e) => handleChange("shop_visibility", e.target.value)}
          className="w-full rounded-md border px-3 py-2 text-sm bg-white"
          data-testid="shop-visibility-select"
        >
          <option value="auto">Auto (follow shop setting)</option>
          <option value="show">Always show online</option>
          <option value="hide">Hide from online shop</option>
        </select>
      </div>

      {/* Product Status Section */}
      <div className="p-4 border rounded-lg bg-gray-50">
        <Label className="text-base font-semibold mb-3 block">Product Status</Label>
        
        <div className="grid grid-cols-2 gap-3">
          {/* Active */}
          <div 
            onClick={() => handleStatusChange('active')}
            className={`p-3 border-2 rounded-lg cursor-pointer transition-all ${
              currentStatus === 'active' 
                ? 'border-green-500 bg-green-50' 
                : 'border-gray-200 hover:border-green-300'
            }`}
            data-testid="status-active"
          >
            <div className="flex items-center gap-2">
              <ShieldCheck className={`h-5 w-5 ${currentStatus === 'active' ? 'text-green-600' : 'text-gray-400'}`} />
              <span className="font-medium">Active</span>
            </div>
            <p className="text-xs text-gray-500 mt-1">Normal - can sell and order</p>
          </div>

          {/* Blocked */}
          <div 
            onClick={() => handleStatusChange('blocked')}
            className={`p-3 border-2 rounded-lg cursor-pointer transition-all ${
              currentStatus === 'blocked' 
                ? 'border-red-500 bg-red-50' 
                : 'border-gray-200 hover:border-red-300'
            }`}
            data-testid="status-blocked"
          >
            <div className="flex items-center gap-2">
              <Ban className={`h-5 w-5 ${currentStatus === 'blocked' ? 'text-red-600' : 'text-gray-400'}`} />
              <span className="font-medium">Blocked</span>
            </div>
            <p className="text-xs text-gray-500 mt-1">Cannot sell, cannot order</p>
          </div>

          {/* Discontinued */}
          <div 
            onClick={() => handleStatusChange('discontinued')}
            className={`p-3 border-2 rounded-lg cursor-pointer transition-all ${
              currentStatus === 'discontinued' 
                ? 'border-orange-500 bg-orange-50' 
                : 'border-gray-200 hover:border-orange-300'
            }`}
            data-testid="status-discontinued"
          >
            <div className="flex items-center gap-2">
              <AlertTriangle className={`h-5 w-5 ${currentStatus === 'discontinued' ? 'text-orange-600' : 'text-gray-400'}`} />
              <span className="font-medium">Discontinued</span>
            </div>
            <p className="text-xs text-gray-500 mt-1">Can sell (clear stock), cannot order</p>
          </div>

          {/* Temp Blocked */}
          <div 
            onClick={() => handleStatusChange('temp_blocked')}
            className={`p-3 border-2 rounded-lg cursor-pointer transition-all ${
              currentStatus === 'temp_blocked' 
                ? 'border-yellow-500 bg-yellow-50' 
                : 'border-gray-200 hover:border-yellow-300'
            }`}
            data-testid="status-temp-blocked"
          >
            <div className="flex items-center gap-2">
              <Clock className={`h-5 w-5 ${currentStatus === 'temp_blocked' ? 'text-yellow-600' : 'text-gray-400'}`} />
              <span className="font-medium">Temp Blocked</span>
            </div>
            <p className="text-xs text-gray-500 mt-1">Cannot sell, can order</p>
          </div>
        </div>

        {/* Status Reason - show if not active */}
        {currentStatus !== 'active' && (
          <div className="mt-3">
            <Label htmlFor="status_reason" className="text-sm">Reason for status change</Label>
            <Input
              id="status_reason"
              value={formData.status_reason || ''}
              onChange={(e) => handleChange('status_reason', e.target.value)}
              placeholder="e.g., Quality issue, Supplier discontinued, Stock audit..."
              className="mt-1"
              data-testid="status-reason-input"
            />
          </div>
        )}

        {/* Status info */}
        {formData.status_changed_at && (
          <div className="mt-2 text-xs text-gray-500">
            Last changed: {new Date(formData.status_changed_at).toLocaleString()} 
            {formData.status_changed_by && ` by ${formData.status_changed_by}`}
          </div>
        )}
      </div>

      {/* Other Settings */}
      <div className="space-y-3">
        <Label className="text-base font-semibold">Other Settings</Label>

        <div className="flex items-start space-x-2 rounded-md border border-purple-200 bg-purple-50 p-3">
          <Checkbox
            id="is_direct_code"
            checked={formData.is_direct_code || false}
            onCheckedChange={(checked) => handleChange("is_direct_code", checked)}
            data-testid="product-is-direct-code-checkbox"
          />
          <div>
            <Label htmlFor="is_direct_code" className="font-medium">Direct order code</Label>
            <p className="text-xs text-gray-600">Generic code (e.g. DIRECT IRRIGATION). On orders and at the till a pop-up asks for the item's own description, qty and price. The invoice shows that description.</p>
          </div>
        </div>
        
        <div className="flex items-center space-x-2">
          <Checkbox
            id="print_shelf_talker_on_receiving"
            checked={formData.print_shelf_talker_on_receiving || false}
            onCheckedChange={(checked) => handleChange("print_shelf_talker_on_receiving", checked)}
          />
          <Label htmlFor="print_shelf_talker_on_receiving" className="font-normal">
            Print Shelf Talker when receiving stock
          </Label>
        </div>

        <div className="flex items-start space-x-2">
          <Checkbox
            id="item_price_sticker"
            checked={formData.item_price_sticker || false}
            onCheckedChange={(checked) => handleChange("item_price_sticker", checked)}
          />
          <div className="grid gap-1.5 leading-none">
            <Label htmlFor="item_price_sticker" className="font-normal">
              Print Item Price Stickers (qty = qty received)
            </Label>
            {formData.barcode && (
              <p className="text-xs text-slate-500">
                This product already has a scannable barcode — enable only if
                you still want a printed price sticker on each unit.
              </p>
            )}
          </div>
        </div>

        <div className="flex items-center space-x-2">
          <Checkbox
            id="quick_access"
            checked={formData.quick_access || false}
            onCheckedChange={(checked) => handleChange("quick_access", checked)}
          />
          <Label htmlFor="quick_access" className="font-normal">
            Show in POS Quick Access
          </Label>
        </div>
      </div>

      {/* Stock Reservation */}
      <div className="space-y-3 border rounded-lg p-4 bg-amber-50/50 border-amber-200">
        <Label className="text-base font-semibold flex items-center gap-2">
          <Clock className="h-4 w-4 text-amber-600" />
          Stock Reservation
        </Label>
        <p className="text-xs text-gray-500">
          Block a quantity for a customer. Reserved stock won't be available for other sales.
          Available to sell: <strong>{Math.max(0, (formData.current_stock || 0) - (formData.reserved_stock || 0))}</strong> of {formData.current_stock || 0}
        </p>
        <div className="grid grid-cols-3 gap-3">
          <div>
            <Label className="text-xs">Qty Reserved</Label>
            <Input
              type="number"
              min="0"
              max={formData.current_stock || 0}
              value={formData.reserved_stock || ""}
              onChange={(e) => handleChange("reserved_stock", parseInt(e.target.value) || 0)}
              placeholder="0"
              data-testid="reserved-stock-input"
            />
          </div>
          <div>
            <Label className="text-xs">Reserved For (Customer)</Label>
            <Input
              value={formData.reserved_for || ""}
              onChange={(e) => handleChange("reserved_for", e.target.value)}
              placeholder="Customer name"
              data-testid="reserved-for-input"
            />
          </div>
          <div>
            <Label className="text-xs">Reservation Note</Label>
            <Input
              value={formData.reservation_note || ""}
              onChange={(e) => handleChange("reservation_note", e.target.value)}
              placeholder="e.g. Picking up Friday"
              data-testid="reservation-note-input"
            />
          </div>
        </div>
        {formData.reserved_stock > 0 && (
          <div className="text-xs text-amber-700 bg-amber-100 rounded px-3 py-2">
            <strong>{formData.reserved_stock}</strong> units reserved
            {formData.reserved_for ? ` for ${formData.reserved_for}` : ''}
            {formData.reservation_note ? ` — ${formData.reservation_note}` : ''}
          </div>
        )}
      </div>

      <div>
        <Label>Notes</Label>
        <textarea
          className="w-full border rounded p-2 min-h-[100px]"
          value={formData.notes || ""}
          onChange={(e) => handleChange("notes", e.target.value)}
        />
      </div>
    </div>
  );
};
